import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import {
  playNotificationSound,
  stopSoundLoop,
  unlockAudio,
} from '../notificationSound';
import { speakRepeated, stopVoice } from '../voice';

const NotificacionesContext = createContext();

const STORAGE_KEY = 'agorax_notificaciones';
const SONIDO_KEY = 'agorax_sonido_activo';
// Tope en memoria para que un panel abierto todo el día no acumule miles de
// entradas (fuga de memoria); la copia persistida ya se recorta a 50.
const MAX_NOTIFICACIONES = 100;

// Convierte cualquier valor a texto plano para poder renderizarlo como hijo de React.
// Cubre el caso de errores de validacion de FastAPI (422), cuyo `detail` es un array
// de objetos {type, loc, msg, input}: sin esto, React lanza el error #31
// ("Objects are not valid as a React child").
const aTexto = (v) => {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) {
    return v.map(aTexto).filter(Boolean).join(', ');
  }
  if (typeof v === 'object') {
    if (typeof v.msg === 'string' && v.msg) return v.msg;
    if (typeof v.message === 'string' && v.message) return v.message;
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v);
};

const sanitizarNotificacion = (n) => {
  if (!n || typeof n !== 'object') return null;
  return {
    ...n,
    titulo: aTexto(n.titulo),
    mensaje: aTexto(n.mensaje),
    voz: typeof n.voz === 'string' ? n.voz : undefined,
  };
};

const cargarNotificaciones = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return [];
    const parsed = JSON.parse(saved);
    if (!Array.isArray(parsed)) return [];
    // Sanea notificaciones ya persistidas (p.ej. un `detail` de 422 guardado antes
    // del fix) para que la campanita nunca vuelva a crashear al renderizarlas.
    return parsed.map(sanitizarNotificacion).filter(Boolean);
  } catch {
    return [];
  }
};

const guardarNotificaciones = (notifs) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(notifs.slice(0, 50)));
  } catch {}
};

const cargarSonido = () => {
  try {
    return localStorage.getItem(SONIDO_KEY) !== 'off';
  } catch {
    return true;
  }
};

export const NotificacionesProvider = ({ children }) => {
  const [notificaciones, setNotificaciones] = useState(cargarNotificaciones);
  const [sonidoActivo, setSonidoActivoState] = useState(cargarSonido);
  const [alertaCritica, setAlertaCritica] = useState(null);

  const sonidoActivoRef = useRef(sonidoActivo);
  sonidoActivoRef.current = sonidoActivo;

  useEffect(() => {
    guardarNotificaciones(notificaciones);
  }, [notificaciones]);

  // Solicitar permiso de notificaciones del navegador una sola vez.
  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      try {
        Notification.requestPermission();
      } catch {}
    }
  }, []);

  // Desbloquear audio ante la primera interaccion del usuario (autoplay policy).
  useEffect(() => {
    const unlock = () => unlockAudio();
    const opts = { once: true, passive: true };
    window.addEventListener('pointerdown', unlock, opts);
    window.addEventListener('keydown', unlock, opts);
    window.addEventListener('touchstart', unlock, opts);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
      window.removeEventListener('touchstart', unlock);
    };
  }, []);

  const agregarNotificacion = useCallback((notif) => {
    const nueva = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      leida: false,
      ...notif,
      titulo: aTexto(notif && notif.titulo),
      mensaje: aTexto(notif && notif.mensaje),
    };
    setNotificaciones((prev) => [nueva, ...prev].slice(0, MAX_NOTIFICACIONES));
  }, []);

  const detenerSonido = useCallback(() => {
    stopSoundLoop();
    stopVoice();
  }, []);

  // Descarta la alerta critica actual: detiene el loop y limpia el banner.
  const descartarAlerta = useCallback(() => {
    stopSoundLoop();
    stopVoice();
    setAlertaCritica(null);
  }, []);

  // Notificacion completa: lista + voz + toast del navegador (respeta el mute).
  const notificar = useCallback(
    (notif) => {
      const severidad = notif.severidad || 'normal';
      agregarNotificacion(notif);
      // Anuncio por VOZ: frase `voz` (o `titulo` como respaldo). Las criticas
      // (cocina/armado/delivery/salio de cocina) se repiten 3 veces; el resto 1.
      // Al terminar la secuencia critica, se cierra la ventana de aviso.
      const texto = notif.voz || notif.titulo || '';
      if (sonidoActivoRef.current && texto) {
        if (severidad === 'critica') speakRepeated(texto, 3, () => setAlertaCritica(null));
        else speakRepeated(texto, 1);
      }
      if (severidad === 'critica') {
        setAlertaCritica({
          id: Date.now(),
          titulo: notif.titulo,
          mensaje: notif.mensaje,
          pedidoId: notif.pedidoId,
          page: notif.page,
          area: notif.area,
        });
      }
      if (
        typeof Notification !== 'undefined' &&
        Notification.permission === 'granted' &&
        notif.titulo
      ) {
        try {
          new Notification(notif.titulo, { body: notif.mensaje || '' });
        } catch {}
      }
    },
    [agregarNotificacion]
  );

  // Solo reproduce sonido (para mensajes/asesor, sin ensuciar la lista).
  const sonar = useCallback((severidad = 'normal', origen) => {
    if (!sonidoActivoRef.current) return;
    playNotificationSound(severidad, origen);
  }, []);

  const toggleSonido = useCallback(() => {
    setSonidoActivoState((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SONIDO_KEY, next ? 'on' : 'off');
      } catch {}
      if (!next) { stopSoundLoop(); stopVoice(); }
      return next;
    });
  }, []);

  const marcarTodasLeidas = useCallback(() => {
    setNotificaciones((prev) =>
      prev.map((n) => (n.leida !== true ? { ...n, leida: true } : n))
    );
    setAlertaCritica(null);
    stopSoundLoop();
    stopVoice();
  }, []);

  // Marca como atendidas todas las notificaciones de una pagina del sidebar.
  const marcarLeidasPorPage = useCallback((page) => {
    setNotificaciones((prev) =>
      prev.map((n) => (n.page === page && n.leida !== true ? { ...n, leida: true } : n))
    );
  }, []);

  // Marca como atendidas las notificaciones de un area (tab del panel).
  const marcarLeidasPorArea = useCallback((area) => {
    const areas = Array.isArray(area) ? area : [area];
    const set = new Set(areas.filter(Boolean));
    setNotificaciones((prev) =>
      prev.map((n) => (set.has(n.area) && n.leida !== true ? { ...n, leida: true } : n))
    );
  }, []);

  const noLeidas = notificaciones.filter((n) => n.leida !== true).length;

  // Pendientes por pagina (badges del sidebar) y por area (badges de las tabs).
  const noLeidasPorPage = {};
  const noLeidasPorArea = {};
  notificaciones.forEach((n) => {
    if (n.leida === true) return;
    if (n.page) noLeidasPorPage[n.page] = (noLeidasPorPage[n.page] || 0) + 1;
    if (n.area) noLeidasPorArea[n.area] = (noLeidasPorArea[n.area] || 0) + 1;
  });

  const valor = {
    notificaciones,
    agregarNotificacion,
    notificar,
    sonar,
    detenerSonido,
    descartarAlerta,
    alertaCritica,
    sonidoActivo,
    toggleSonido,
    marcarTodasLeidas,
    marcarLeidasPorPage,
    marcarLeidasPorArea,
    noLeidas,
    noLeidasPorPage,
    noLeidasPorArea,
  };

  return <NotificacionesContext.Provider value={valor}>{children}</NotificacionesContext.Provider>;
};

export const useNotificaciones = () => useContext(NotificacionesContext);
